import {
  Directive,
  ElementRef,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChildren
} from '@angular/core'
import { UntypedFormGroup } from '@angular/forms'
import { ActivatedRoute } from '@angular/router'
import {
  Address,
  Country,
  EventService,
  GlobalMessageService,
  GlobalMessageType,
  Region,
  SiteConnector,
  WindowRef
} from '@spartacus/core'
import { of, Subscription } from 'rxjs'
import { MbFloatingLabelDirective } from 'src/app/mb/shared/directives/mb-floating-label.directive'
import { MbConstants } from 'src/app/mb/shared/mb-constants'
import { MbCheckoutCommonService } from '../../../services/mb-checkout-common.service'
import { MbCheckoutStepService } from '../../../services/mb-checkout-step.service'
import { MbCheckoutDeliveryFacade } from '../../../facades/mb-checkout-delivery.facade'
import { filter, map, switchMap, tap, withLatestFrom } from 'rxjs/operators'
import { MbCheckoutShippingFacade } from '../../../facades/mb-checkout-shipping.facade'
import { BreakpointObserver } from '@angular/cdk/layout'
import { CheckoutDeliveryAddressSetEvent } from '@spartacus/checkout/base/root'
import { ActiveCartFacade } from '@spartacus/cart/base/root'
import { CheckoutDeliveryAddressSetFailEvent } from '../../../../analytics/events/mb-checkout.events'

@Directive()
export abstract class MbCheckoutShippingAddressFormBase implements OnInit, OnDestroy {

  @ViewChildren(MbFloatingLabelDirective) floatingLabels: QueryList<MbFloatingLabelDirective> | undefined

  subscription: Subscription = new Subscription()
  country: Country | undefined
  shippingAddressForm: UntypedFormGroup = this.convertShippingAddressModel2Form()
  shippingAddress: Address = {}
  displaySimpleButtons = false
  showAddNewAddressForm = false
  shouldLeavePage = false
  constants = MbConstants
  regions: Region[] = []
  selectedRegion: Region = { name: '', isocode: '' }
  isNotMobile$ = this.breakpointObserver.observe('(min-width: 576px)').pipe(
    map(bp => bp?.matches)
  )

  constructor (protected activatedRoute: ActivatedRoute,
               protected siteConnector: SiteConnector,
               protected mbCheckoutStepService: MbCheckoutStepService,
               protected mbCheckoutCommonService: MbCheckoutCommonService,
               protected mbCheckoutShippingFacade: MbCheckoutShippingFacade,
               protected globalMessageService: GlobalMessageService,
               protected mbCheckoutDeliveryService: MbCheckoutDeliveryFacade,
               protected activeCartService: ActiveCartFacade,
               protected winRef: WindowRef,
               protected el: ElementRef,
               protected breakpointObserver: BreakpointObserver,
               protected eventService: EventService) {
  }

  ngOnInit () {
    this.subscription?.add(this.activeCartService.getActive().pipe(
      withLatestFrom(this.activeCartService.isGuestCart())
    ).subscribe(([cart, isGuestCart]) => {
        if (this.shouldLeavePage) {
          this.shouldLeavePage = false
          this.mbCheckoutStepService.next(this.activatedRoute)
        }

        if (isGuestCart) {
          this.country = cart?.country

          this.shippingAddress = {
            ...this.shippingAddress,
            ...cart?.deliveryAddress,
            country: this.country
          }

          this.setCountry()
          if (cart?.deliveryAddress?.region?.isocode) {
            this.selectedRegion = cart?.deliveryAddress?.region
          }
          this.shippingAddressForm = this.convertShippingAddressModel2Form()
        }
      }))

    this.subscription?.add(this.mbCheckoutCommonService.userCountry.pipe(
        withLatestFrom(this.activeCartService.isGuestCart()),
        filter(([userCountry, isGuestCart]) => !!userCountry && !isGuestCart),
        switchMap(([userCountry]) => {
          this.country = userCountry
          this.setCountry()
          this.shippingAddressForm = this.convertShippingAddressModel2Form()
          if (userCountry?.isocode) {
            return this.siteConnector.getRegions(userCountry?.isocode)
          } else {
            return of([])
          }
        })).subscribe((regions: Region[]) => {
        if (regions) {
          this.regions = [...regions]
        }
      })
    )

    this.subscription?.add(this.mbCheckoutShippingFacade.isDisplaySimpleButtons.pipe(
      tap(isDisplaySimpleButtons => this.displaySimpleButtons = isDisplaySimpleButtons)
    ).subscribe())

    this.subscription?.add(this.mbCheckoutShippingFacade.isAddNewAddressesFormVisible.pipe(
      tap(showAddNewAddressForm => this.showAddNewAddressForm = showAddNewAddressForm)
    ).subscribe())
  }

  ngOnDestroy () {
    this.subscription?.unsubscribe()
  }

  setCountry (): void {
    this.shippingAddress.country = this.country
    this.shippingAddressForm.patchValue({ country: this.country })
    this.isCountryDisabled()
    this.convertShippingAddressModel2Form()
  }

  saveAndContinue (): void {
    if (!this.shippingAddressForm.valid) {
      this.shippingAddressForm.markAllAsTouched()
      this.mbCheckoutCommonService.scrollToError(this.shippingAddressForm, this.el, this.winRef)
    } else {
      this.convertShippingAddressForm2Model()
      this.addAddress(this.shippingAddress)
    }
  }

  cancelAddNewAddressForm () {
    if (!this.displaySimpleButtons) {
      this.mbCheckoutStepService.back(this.activatedRoute)
    } else {
      this.mbCheckoutShippingFacade.cancelAddNewAddressForm()
    }
    this.winRef.nativeWindow?.scroll({ top: 0, behavior: 'smooth' })
  }

  isCountryDisabled (): boolean {
    if (this.country) {
      this.shippingAddressForm?.patchValue({ country: this.country?.name })
      this.shippingAddressForm?.get(this.constants.COUNTRY_CODE)?.disable()
      return true
    } else {
      this.shippingAddressForm?.patchValue({ country: this.constants.COUNTRY })
      this.shippingAddressForm?.get(this.constants.COUNTRY_CODE)?.enable()
      return false
    }
  }

  addAddress (address?: Address) {
    if (address) {
      this.mbCheckoutDeliveryService.createAndSetAddressWithoutDeliveryMode(address)

      this.subscription?.add(this.eventService.get(CheckoutDeliveryAddressSetEvent).pipe(
        withLatestFrom(this.activeCartService.isGuestCart())
        ).subscribe(([event, isGuestCart]) => {
          if (!isGuestCart) {
            this.mbCheckoutShippingFacade.cancelAddNewAddressForm()
          }
          this.shouldLeavePage = true
        })
      )

      this.subscription?.add(this.eventService.get(CheckoutDeliveryAddressSetFailEvent)
        .subscribe(payload => {
          console.error(payload?.error)
          this.globalMessageService.add({ key: 'address.error' }, GlobalMessageType.MSG_TYPE_ERROR)
        }))
    } else {
      this.globalMessageService.add({ key: 'address.error' }, GlobalMessageType.MSG_TYPE_ERROR)
    }
  }

  abstract convertShippingAddressModel2Form (): UntypedFormGroup

  abstract convertShippingAddressForm2Model (): void

}
